<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  standalone: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'created']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const agentPhone = ref('');
const agentDisplayName = ref('');
const agentAvailability = ref('online');
const selectedRoleId = ref('agent');
const teamRoles = ref({});

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentAvailability,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const teamUIFlags = useMapGetter('teams/getUIFlags');
const teams = useMapGetter('teams/getTeams');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const sortedTeams = computed(() =>
  [...teams.value].sort((firstTeam, secondTeam) =>
    String(firstTeam.name || '').localeCompare(String(secondTeam.name || ''))
  )
);

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const avatarLabel = computed(() =>
  (
    agentDisplayName.value ||
    agentName.value ||
    t('AGENT_MGMT.ADD.PREVIEW_NAME')
  )
    .trim()
    .slice(0, 2)
    .toUpperCase()
);

const selectedTeamCount = computed(
  () => Object.values(teamRoles.value).filter(Boolean).length
);

const teamAssignments = computed(() =>
  Object.entries(teamRoles.value)
    .filter(([, role]) => role)
    .map(([teamId, role]) => ({ team_id: Number(teamId), role }))
);

const isTeamRoleEnabled = (teamId, role) => teamRoles.value[teamId] === role;

const toggleTeamRole = (teamId, role, enabled) => {
  teamRoles.value = {
    ...teamRoles.value,
    [teamId]: enabled ? role : null,
  };
};

const close = () => {
  if (!uiFlags.value.isCreating) emit('close');
};

const closeFromBackdrop = () => {
  if (!props.standalone) close();
};

const onKeydown = event => {
  if (event.key === 'Escape' && !uiFlags.value.isCreating) close();
};

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  store
    .dispatch('teams/get')
    .catch(() => useAlert(t('AGENT_MGMT.ADD.TEAMS.LOAD_ERROR')));
});

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value.trim(),
      email: agentEmail.value.trim(),
      display_name: agentDisplayName.value.trim(),
      phone_number: agentPhone.value.trim(),
      availability: agentAvailability.value,
      team_assignments: teamAssignments.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
      payload.role = 'agent';
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('created');
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <Teleport to="body" :disabled="standalone">
    <div
      class="flex w-full justify-center"
      :class="
        standalone
          ? 'relative items-start'
          : 'fixed inset-0 z-[9999] items-end bg-n-alpha-black2 backdrop-blur-[4px] sm:items-center sm:p-4'
      "
      @mousedown.self="closeFromBackdrop"
    >
      <form
        class="flex w-full max-w-[1120px] flex-col overflow-hidden bg-n-background"
        :class="
          standalone
            ? 'rounded-lg border border-n-weak shadow-sm'
            : 'max-h-[100dvh] shadow-xl sm:max-h-[calc(100dvh-2rem)] sm:rounded-lg'
        "
        role="dialog"
        aria-modal="true"
        :aria-label="$t('AGENT_MGMT.ADD.TITLE')"
        @submit.prevent="addAgent"
      >
        <header
          class="acl-mobile-account-menu-host flex flex-shrink-0 items-start justify-between gap-4 border-b border-n-weak px-5 py-4 sm:px-7 sm:py-5"
        >
          <div class="min-w-0">
            <h2 class="m-0 text-xl font-semibold text-n-slate-12">
              {{ $t('AGENT_MGMT.ADD.TITLE') }}
            </h2>
            <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-11">
              {{ $t('AGENT_MGMT.ADD.DESC') }}
            </p>
          </div>
          <Button
            v-if="!standalone"
            ghost
            slate
            type="button"
            icon="i-lucide-x"
            :disabled="uiFlags.isCreating"
            :aria-label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click="close"
          />
        </header>

        <div
          class="flex flex-1 flex-col"
          :class="standalone ? 'overflow-visible' : 'min-h-0 overflow-y-auto'"
        >
          <section
            class="flex flex-col border-b border-n-weak px-5 py-6 sm:px-7"
          >
            <div class="mb-6 flex items-center gap-4">
              <div
                class="flex size-16 flex-shrink-0 items-center justify-center rounded-full bg-n-brand text-lg font-semibold text-white"
              >
                {{ avatarLabel }}
              </div>
              <div class="min-w-0">
                <span
                  class="block truncate text-base font-semibold text-n-slate-12"
                >
                  {{
                    agentDisplayName ||
                    agentName ||
                    $t('AGENT_MGMT.ADD.PREVIEW_NAME')
                  }}
                </span>
                <span
                  class="mt-1 inline-flex rounded-full bg-n-slate-3 px-3 py-1 text-xs font-medium text-n-slate-11"
                >
                  {{ selectedRole?.label }}
                </span>
              </div>
            </div>

            <div class="grid gap-x-4 gap-y-4 sm:grid-cols-2">
              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.agentName.$error }"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
                <input
                  v-model="agentName"
                  type="text"
                  autocomplete="name"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
                  @input="v$.agentName.$touch"
                />
                <span v-if="v$.agentName.$error" class="message">
                  {{ $t('AGENT_MGMT.ADD.FORM.NAME.ERROR') }}
                </span>
              </label>

              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.agentEmail.$error }"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
                <input
                  v-model="agentEmail"
                  type="email"
                  autocomplete="email"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
                  @input="v$.agentEmail.$touch"
                />
                <span v-if="v$.agentEmail.$error" class="message">
                  {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.ERROR') }}
                </span>
              </label>

              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.PHONE.LABEL') }}
                <input
                  v-model="agentPhone"
                  type="tel"
                  autocomplete="tel"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="$t('AGENT_MGMT.ADD.FORM.PHONE.PLACEHOLDER')"
                />
              </label>

              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.DISPLAY_NAME.LABEL') }}
                <input
                  v-model="agentDisplayName"
                  type="text"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-9 focus:border-n-brand"
                  :placeholder="
                    $t('AGENT_MGMT.ADD.FORM.DISPLAY_NAME.PLACEHOLDER')
                  "
                />
              </label>

              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.selectedRoleId.$error }"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
                <select
                  v-model="selectedRoleId"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
                  @change="v$.selectedRoleId.$touch"
                >
                  <option v-for="role in roles" :key="role.id" :value="role.id">
                    {{ role.label }}
                  </option>
                </select>
              </label>

              <label
                class="m-0 block min-w-0 text-sm font-medium text-n-slate-12"
                :class="{ error: v$.agentAvailability.$error }"
              >
                {{ $t('AGENT_MGMT.ADD.FORM.AVAILABILITY.LABEL') }}
                <select
                  v-model="agentAvailability"
                  class="reset-base mt-2 h-11 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand"
                  @change="v$.agentAvailability.$touch"
                >
                  <option value="online">
                    {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE') }}
                  </option>
                  <option value="busy">
                    {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY') }}
                  </option>
                  <option value="offline">
                    {{
                      $t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE')
                    }}
                  </option>
                </select>
              </label>
            </div>

            <div
              class="mt-6 flex items-start gap-3 border-t border-n-weak pt-5 text-sm text-n-slate-11"
            >
              <span
                class="i-lucide-mail-check mt-0.5 size-4 flex-shrink-0 text-n-brand"
              />
              <p class="m-0 leading-5">
                {{ $t('AGENT_MGMT.ADD.INVITATION_NOTICE') }}
              </p>
            </div>
          </section>

          <section class="flex flex-col px-5 py-6 sm:px-7">
            <div class="flex items-center justify-between gap-3">
              <div>
                <h3 class="m-0 text-base font-semibold text-n-slate-12">
                  {{ $t('AGENT_MGMT.ADD.TEAMS.TITLE') }}
                </h3>
                <p class="mb-0 mt-1 text-sm leading-5 text-n-slate-11">
                  {{ $t('AGENT_MGMT.ADD.TEAMS.DESCRIPTION') }}
                </p>
              </div>
              <span
                class="flex-shrink-0 rounded-full bg-n-slate-3 px-2.5 py-1 text-xs font-medium text-n-slate-11"
              >
                {{
                  $t('AGENT_MGMT.ADD.TEAMS.SELECTED_COUNT', {
                    count: selectedTeamCount,
                  })
                }}
              </span>
            </div>

            <div
              v-if="teamUIFlags.isFetching"
              class="flex min-h-40 items-center justify-center"
            >
              <Spinner />
            </div>

            <div
              v-else-if="sortedTeams.length"
              class="mt-6 flex flex-col gap-5"
            >
              <section class="overflow-hidden rounded-lg border border-n-weak">
                <div class="border-b border-n-weak bg-n-slate-2 px-4 py-3">
                  <h4 class="m-0 text-base font-semibold text-n-slate-12">
                    {{ $t('AGENT_MGMT.ADD.TEAMS.USER.TITLE') }}
                  </h4>
                  <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                    {{ $t('AGENT_MGMT.ADD.TEAMS.USER.DESCRIPTION') }}
                  </p>
                </div>
                <div class="divide-y divide-n-weak px-4">
                  <div
                    v-for="team in sortedTeams"
                    :key="`user-${team.id}`"
                    class="flex min-h-10 items-center justify-between gap-3 py-2"
                  >
                    <span class="min-w-0 break-words text-sm text-n-slate-12">
                      {{ team.name }}
                    </span>
                    <ToggleSwitch
                      :model-value="isTeamRoleEnabled(team.id, 'user')"
                      :aria-label="
                        $t('AGENT_MGMT.ADD.TEAMS.USER.TOGGLE', {
                          team: team.name,
                        })
                      "
                      @update:model-value="
                        toggleTeamRole(team.id, 'user', $event)
                      "
                    />
                  </div>
                </div>
              </section>

              <section class="overflow-hidden rounded-lg border border-n-weak">
                <div class="border-b border-n-weak bg-n-slate-2 px-4 py-3">
                  <h4 class="m-0 text-base font-semibold text-n-slate-12">
                    {{ $t('AGENT_MGMT.ADD.TEAMS.SUPERVISOR.TITLE') }}
                  </h4>
                  <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                    {{ $t('AGENT_MGMT.ADD.TEAMS.SUPERVISOR.DESCRIPTION') }}
                  </p>
                </div>
                <div class="divide-y divide-n-weak px-4">
                  <div
                    v-for="team in sortedTeams"
                    :key="`supervisor-${team.id}`"
                    class="flex min-h-10 items-center justify-between gap-3 py-2"
                  >
                    <span class="min-w-0 break-words text-sm text-n-slate-12">
                      {{ team.name }}
                    </span>
                    <ToggleSwitch
                      :model-value="isTeamRoleEnabled(team.id, 'supervisor')"
                      :aria-label="
                        $t('AGENT_MGMT.ADD.TEAMS.SUPERVISOR.TOGGLE', {
                          team: team.name,
                        })
                      "
                      @update:model-value="
                        toggleTeamRole(team.id, 'supervisor', $event)
                      "
                    />
                  </div>
                </div>
              </section>
            </div>

            <p v-else class="mb-0 mt-6 text-sm text-n-slate-11">
              {{ $t('AGENT_MGMT.ADD.TEAMS.EMPTY') }}
            </p>
          </section>
        </div>

        <footer
          class="flex flex-shrink-0 items-center justify-end gap-2 border-t border-n-weak bg-n-background px-5 py-4 sm:px-7"
        >
          <Button
            faded
            slate
            type="button"
            :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            :disabled="uiFlags.isCreating"
            @click="close"
          />
          <Button
            type="submit"
            icon="i-lucide-user-round-plus"
            :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isCreating"
            :is-loading="uiFlags.isCreating"
          />
        </footer>
      </form>
    </div>
  </Teleport>
</template>
